import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X, PlusSquare } from "lucide-react";

const DISMISS_KEY = "utile_install_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => window.matchMedia("(max-width: 1024px)").matches && "ontouchstart" in window;
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

export default function InstallAppBanner() {
  const { pathname } = useLocation();
  const [prompt, setPrompt] = useState(null);
  const [mode, setMode] = useState(null);

  useEffect(() => {
    if (isStandalone() || snoozed() || !isMobile()) return;
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); setMode("android"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = isIOS() ? setTimeout(() => setMode("ios"), 6000) : null;
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setMode(null); };
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice.catch(() => null);
    setPrompt(null);
    dismiss();
  };

  if (!mode || pathname.startsWith("/admin") || pathname.startsWith("/pagamento")) return null;

  return (
    <div data-testid="install-app-banner" className="fixed inset-x-3 bottom-20 z-[70] animate-in slide-in-from-bottom-6 fade-in duration-500">
      <div className="relative flex items-start gap-3 bg-[#0e0e0e]/95 backdrop-blur-xl border border-[#22ff66]/40 rounded-2xl p-4 shadow-2xl text-white">
        <img src="/icon-192.png?v=3" alt="+UTILE" className="shrink-0 w-12 h-12 rounded-xl bg-white object-contain" />
        <div className="min-w-0 flex-1 pr-5">
          <div className="text-sm font-bold">Installa l'app +UTILE</div>
          {mode === "ios" ? (
            <div data-testid="install-ios-steps" className="text-xs text-white/70 mt-1 leading-relaxed">
              Tocca <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#22ff66]" /> <b>Condividi</b>, poi <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5 text-[#22ff66]" /> <b>Aggiungi a Home</b>.
            </div>
          ) : (
            <>
              <div className="text-xs text-white/70 mt-1">Prenota più velocemente, direttamente dalla schermata home.</div>
              <button type="button" data-testid="install-app-btn" onClick={install} className="mt-3 inline-flex items-center gap-2 bg-[#22ff66] text-black px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold active:scale-95 transition-transform">
                <Download className="w-3.5 h-3.5" /> Installa
              </button>
            </>
          )}
        </div>
        <button type="button" data-testid="install-banner-close" onClick={dismiss} aria-label="Chiudi" className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
